import { useEffect, useState } from 'react'

type CountdownTime = {
  hours: number
  minutes: number
  seconds: number
}

const COUNTDOWN_KEY = 'chakras_countdown_end'
const COUNTDOWN_DURATION = (4 * 60 + 45) * 60 * 1000
const INITIAL_TIME: CountdownTime = { hours: 4, minutes: 44, seconds: 50 }

let endTime: number | null = null
let intervalId: ReturnType<typeof setInterval> | null = null
const subscribers = new Set<(time: CountdownTime) => void>()

export function useCountdown() {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(INITIAL_TIME)

  useEffect(() => {
    subscribers.add(setTimeLeft)
    publishTime()
    startClock()

    return () => {
      subscribers.delete(setTimeLeft)
      if (subscribers.size === 0 && intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    }
  }, [])

  return timeLeft
}

function startClock() {
  if (intervalId) return

  intervalId = setInterval(publishTime, 1000)
}

function publishTime() {
  const time = getTimeRemaining(getEndTime())
  subscribers.forEach((subscriber) => subscriber(time))

  if (
    time.hours === 0 &&
    time.minutes === 0 &&
    time.seconds === 0 &&
    intervalId
  ) {
    clearInterval(intervalId)
    intervalId = null
  }
}

function getEndTime() {
  if (endTime) return endTime

  const fallbackEndTime = Date.now() + COUNTDOWN_DURATION
  if (typeof window === 'undefined') {
    endTime = fallbackEndTime
    return endTime
  }

  try {
    const storedEndTime = Number(window.localStorage.getItem(COUNTDOWN_KEY))
    endTime = storedEndTime > Date.now() ? storedEndTime : fallbackEndTime
    window.localStorage.setItem(COUNTDOWN_KEY, endTime.toString())
  } catch {
    endTime = fallbackEndTime
  }

  return endTime
}

function getTimeRemaining(targetTime: number): CountdownTime {
  const difference = Math.max(0, targetTime - Date.now())

  return {
    hours: Math.floor(difference / (60 * 60 * 1000)),
    minutes: Math.floor((difference % (60 * 60 * 1000)) / (60 * 1000)),
    seconds: Math.floor((difference % (60 * 1000)) / 1000),
  }
}
